import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider } from 'primereact/divider';
import { Header } from '@/components/layout';
import { useDocuments } from '@/api/domains/documents';
import { useCreateAnalysis, useTriggerAnalysis } from '@/api/domains/analyses';
import {
  ContextGranularity,
  AnalysisConfig,
  AnalysisCreateRequest
} from '@/api/domains/analyses/types';
import styles from './CreateAnalysis.module.css';
import { ProgressContainer } from './components';
import { BackButton } from '@/components/ui';
import {
  StepConfiguration,
  StepSelectExternal,
  StepSelectInternal
} from './components/AnalysisWizardSteps';
import { ANALYSIS_ENABLED } from '@/config/features';

const CreateAnalysis = () => {
  const navigate = useNavigate();
  const { data: documentsData } = useDocuments({ limit: 200 });
  const createMutation = useCreateAnalysis();
  const triggerMutation = useTriggerAnalysis();

  const [disableSimilaritySearch, setDisableSimilaritySearch] =
    useState<boolean>(ANALYSIS_ENABLED ? false : true);

  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState<
    Omit<AnalysisCreateRequest, 'user_id'>
  >({
    analysis_name: '',
    document_1_id: '',
    document_2_id: '',
    context_granularity: 'PARAGRAPH' as ContextGranularity,
    use_rag: true
  });

  const [config, setConfig] = useState<AnalysisConfig>({
    method: 'HYBRID',
    vector_threshold: 0.5,
    top_k_candidates: 5,
    min_severity: 'LOW',
    min_confidence_score: 0.5
  });

  const documents = documentsData?.documents || [];

  const handleNext = () => {
    if (activeStep < 2) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!ANALYSIS_ENABLED) {
      // should never happen with a disabled button, but guard anyway
      alert('Analysis creation is currently disabled in this deployment.');
      return;
    }

    console.log('FormData: ', formData);
    console.log('config: ', config);
    try {
      const analysis = await createMutation.mutateAsync({
        analysis_name: formData.analysis_name,
        user_id: 'user123',
        document_1_id: formData.document_1_id,
        document_2_id: formData.document_2_id,
        context_granularity: formData.context_granularity,
        use_rag: formData.use_rag
      });

      await triggerMutation.mutateAsync({
        analysisId: analysis.analysis_id,
        config: {
          method: config.method,
          vector_threshold: config.vector_threshold,
          top_k_candidates: config.top_k_candidates,
          min_severity: config.min_severity,
          min_confidence_score: config.min_confidence_score
        }
      });

      navigate(`/analyses/${analysis.analysis_id}`);
    } catch (error) {
      console.error('Failed to create analysis:', error);
    }
  };

  const isLoading = createMutation.isPending || triggerMutation.isPending;

  const disabledBanner = !ANALYSIS_ENABLED ? (
    <div className={styles.disabledBanner}>
      <strong>Note:</strong> analysis creation is disabled in this deployment;
      the wizard is for demonstration only and the final button is inactive.
    </div>
  ) : null;

  return (
    <>
      <Header />
      <Divider />
      <div className={styles.root}>
        <div className={styles.main}>
          <BackButton />
          {disabledBanner}
          <ProgressContainer activeStep={activeStep} />

          {activeStep === 0 && (
            <StepSelectExternal
              formData={formData}
              setFormData={setFormData}
              documents={documents}
              handleNext={handleNext}
              disableSimilaritySearch={disableSimilaritySearch}
              setDisableSimilaritySearch={setDisableSimilaritySearch}
            />
          )}

          {activeStep === 1 && (
            <StepSelectInternal
              formData={formData}
              setFormData={setFormData}
              documents={documents}
              handleNext={handleNext}
              handleBack={handleBack}
              disableSimilaritySearch={disableSimilaritySearch}
            />
          )}

          {activeStep === 2 && (
            <StepConfiguration
              config={config}
              onChangeConfig={setConfig}
              handleBack={handleBack}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CreateAnalysis;
